import {
  addDoc as _addDoc,
  updateDoc as _updateDoc,
  deleteDoc as _deleteDoc,
  writeBatch as _writeBatch,
  setDoc as _setDoc,
  doc as _doc,
  runTransaction as _runTransaction,
} from 'firebase/firestore'
import type {
  DocumentReference,
  CollectionReference,
  DocumentData,
  UpdateData,
  WithFieldValue,
  SetOptions,
  PartialWithFieldValue,
  FirestoreError,
  Firestore,
  Transaction,
} from 'firebase/firestore'
import { log, logFile } from '../logger'
import { logAddDoc, logDeleteDoc, logSetDoc, logUpdateDoc, rethrowTagged } from './log'

export function addDoc<T extends DocumentData>(
  ref: CollectionReference<T>,
  data: WithFieldValue<T>,
) {
  logAddDoc(ref.path)
  logFile('fs', 'addDoc', { path: ref.path })
  return _addDoc(ref, data).catch((err: FirestoreError) => {
    const landed = err?.code === 'already-exists' ? alreadyLandedDoc(ref, err) : null
    if (landed) {
      log('fs', 'addDoc already landed — treating the resend as success', { path: landed.path })
      return landed
    }
    return rethrowTagged('addDoc', ref.path)(err)
  })
}

// addDoc creates with a random id and an exists=false precondition, so
// "already exists" can only mean our own earlier send of this same write
// committed but its ack was lost (the write stream reset — typical on resume
// with long polling) and the SDK resent it. The data is saved; failing the
// call would show a false error toast. The error names the document path.
function alreadyLandedDoc<T extends DocumentData>(ref: CollectionReference<T>, err: FirestoreError) {
  const docPath = /\/documents\/(\S+)/.exec(err.message)?.[1]
  const slash = docPath?.lastIndexOf('/') ?? -1
  if (!docPath || docPath.slice(0, slash) !== ref.path) return null
  return _doc(ref, docPath.slice(slash + 1))
}

export function updateDoc<T extends DocumentData>(
  ref: DocumentReference<T>,
  data: UpdateData<T> | PartialWithFieldValue<T>,
) {
  logUpdateDoc(ref.path)
  logFile('fs', 'updateDoc', { path: ref.path })
  return _updateDoc(ref, data as UpdateData<T>).catch(rethrowTagged('updateDoc', ref.path))
}

// One round-trip for several document updates. Takes the writes as data rather
// than handing out a batch, so every path still passes through the logger the
// way the single-doc wrappers do.
export function commitUpdates<T extends DocumentData>(
  firestore: Firestore,
  updates: { ref: DocumentReference<T>; data: UpdateData<T> }[],
) {
  const batch = _writeBatch(firestore)
  for (const { ref, data } of updates) {
    logUpdateDoc(ref.path)
    logFile('fs', 'batchUpdate', { path: ref.path })
    batch.update(ref, data)
  }
  return batch.commit().catch(rethrowTagged('batchUpdate', updates[0]?.ref.path ?? '(empty)'))
}

export type BatchWrite =
  | { kind: 'set'; ref: DocumentReference<DocumentData>; data: DocumentData }
  | { kind: 'update'; ref: DocumentReference<DocumentData>; data: UpdateData<DocumentData> }
  | { kind: 'delete'; ref: DocumentReference<DocumentData> }

export function commitWrites(firestore: Firestore, writes: BatchWrite[]) {
  const batch = _writeBatch(firestore)
  for (const w of writes) {
    if (w.kind === 'set') {
      logSetDoc(w.ref.path)
      logFile('fs', 'batchSet', { path: w.ref.path })
      batch.set(w.ref, w.data)
    } else if (w.kind === 'delete') {
      logDeleteDoc(w.ref.path)
      logFile('fs', 'batchDelete', { path: w.ref.path })
      batch.delete(w.ref)
    } else {
      logUpdateDoc(w.ref.path)
      logFile('fs', 'batchUpdate', { path: w.ref.path })
      batch.update(w.ref, w.data)
    }
  }
  return batch.commit().catch(rethrowTagged('batchWrite', writes[0]?.ref.path ?? '(empty)'))
}

export function deleteDoc(ref: DocumentReference) {
  logDeleteDoc(ref.path)
  logFile('fs', 'deleteDoc', { path: ref.path })
  return _deleteDoc(ref).catch(rethrowTagged('deleteDoc', ref.path))
}

export function setDoc<T extends DocumentData>(
  ref: DocumentReference<T>,
  data: PartialWithFieldValue<T>,
  options: SetOptions,
): Promise<void>
export function setDoc<T extends DocumentData>(
  ref: DocumentReference<T>,
  data: WithFieldValue<T>,
): Promise<void>
export function setDoc<T extends DocumentData>(
  ref: DocumentReference<T>,
  data: WithFieldValue<T> | PartialWithFieldValue<T>,
  options?: SetOptions,
): Promise<void> {
  logSetDoc(ref.path)
  logFile('fs', 'setDoc', { path: ref.path })
  return (options
    ? _setDoc(ref, data as PartialWithFieldValue<T>, options)
    : _setDoc(ref, data as WithFieldValue<T>)
  ).catch(rethrowTagged('setDoc', ref.path))
}

// Transactions read from the server, never the cache — use one when a write must
// not land on the strength of a cached "doesn't exist" snapshot.
export function runTransaction<T>(firestore: Firestore, label: string, fn: (tx: Transaction) => Promise<T>) {
  logFile('fs', 'runTransaction', { label })
  return _runTransaction(firestore, fn).catch(rethrowTagged('runTransaction', label))
}
